import { z } from "zod";

/**
 * TeamDaily — das tagesaktuelle Board eines Service-Bereichs.
 *
 * Ablösung der eigenständigen Hub-App `DailyDashboard`/`TeamDaily`. Fachliche
 * Herkunft der Regeln: deren Server (status-per-ID-JQL, „vom Kunden erstellt" =
 * Reporter ist JSM-Kunde, „heute gebucht" je Mitarbeiter, SF-Projekt→Kunde für
 * die Budgets). Dort aus einer SharePoint-Excel und einer gelernten
 * Kundenfirma-Karte gespeist; hier aus den nativen CRM-Tabellen
 * (`segment_team_memberships`, `employee_absence_days`, `service_projects`) und
 * einem **lesenden** Live-Zugriff auf Jira (`@msc/jira`, `DAILY_BOARD_FIELDS`).
 *
 * Das Board ist in v1 **rein lesend** — keine Zuweisung per Ziehen, kein
 * Rückschreiben nach Jira (die Jira-Read-Only-Grenze des CRM gilt auch hier).
 */

/**
 * Status, in denen ein Vorgang **auf jemand anderen wartet** — beim Kunden,
 * bei einem Dritten, auf einen Termin. Sie zählen nicht in den aktiven Backlog.
 *
 * Verglichen wird der **Anzeigename**, kleingeschrieben und ohne Umlaute. Das
 * ist eine bewusste Ausnahme von der Regel „über Status-Kennungen vergleichen":
 * Welche Status als „wartend" gelten, ist eine **fachliche** Entscheidung des
 * Bereichs, keine technische der Instanz — und die Kennungen einer fremden
 * Jira-Site wären hier weder lesbar noch pflegbar. Der Preis: Wird ein Status
 * in Jira umbenannt, zählt er wieder als aktiv. Deshalb steht die Liste hier
 * und nicht verstreut im Code.
 */
export const TEAMDAILY_WAITING_STATUS: readonly string[] = [
  "warte auf rueckmeldung",
  "wartet auf weiteres",
  "warten auf zulieferung",
  "ausgesetzt",
  "zurueckgestellt",
  "on hold",
  "waiting for customer",
  "waiting for support",
  "blocked",
  // Der Transport liegt beim Kunden: Bis er ihn einspielt, kann hier niemand
  // etwas tun. Der Vorgang steht in Jira offen, ist aber keine Tagesarbeit.
  "to transport",
  "zu transportieren",
];

/** Umlaute auflösen und kleinschreiben — für den Status-Vergleich. */
export function normalizeStatusName(name: string | null): string {
  return (name ?? "")
    .trim()
    .toLowerCase()
    .replace(/ä/g, "ae")
    .replace(/ö/g, "oe")
    .replace(/ü/g, "ue")
    .replace(/ß/g, "ss");
}

/** Wartet dieser Status auf jemand anderen? */
export function isWaitingStatus(statusName: string | null): boolean {
  return TEAMDAILY_WAITING_STATUS.includes(normalizeStatusName(statusName));
}

/**
 * Projektstatus, die als **abgeschlossen** gelten — solche Budgets gehören
 * nicht mehr aufs Board.
 *
 * Der Status ist eine freie Salesforce-Auswahlliste, deshalb wird
 * ausgeschlossen statt eingeschlossen: Ein neuer Status wie „läuft (verlängert)"
 * soll weiter erscheinen, statt still zu verschwinden. Verglichen wird
 * normalisiert (klein, ohne Umlaute).
 */
export const TEAMDAILY_CLOSED_PROJECT_STATUS: readonly string[] = [
  "abgeschlossen",
  "beendet",
  "geschlossen",
  "abgebrochen",
  "storniert",
  "closed",
  "completed",
  "cancelled",
];

/** Ist dieses Projekt abgeschlossen (und damit für das Board erledigt)? */
export function isClosedProjectStatus(status: string | null): boolean {
  return TEAMDAILY_CLOSED_PROJECT_STATUS.includes(normalizeStatusName(status));
}

/**
 * Ab wie vielen Tagen ohne Bewegung ein Vorgang auffällt. **Sieben** — nach
 * einer Woche ohne Anfassen ist ein Servicevorgang liegengeblieben, nicht
 * „in Arbeit" (Vorgabe aus dem Daily, 2026-08-27).
 */
export const TEAMDAILY_STALE_DAYS = 7;

/**
 * Ab wie vielen Tagen seit Anlage ein ungelöster Vorgang „älter als 6 Wochen"
 * ist — die Kachel „older than 6 weeks" des abgelösten All-KPI-Dashboards
 * (`created <= -6w`).
 */
export const TEAMDAILY_ALT_TAGE = 42;

/**
 * Der Status „Ausgesetzt", normalisiert (`normalizeStatusName`). Gezählt wird
 * nie über diesen Namen, sondern über die Kennungen, auf die er im
 * Statuskatalog (`jira_statuses`) einmal abgebildet wird (Spec, Abgleich Nr. 3).
 */
export const TEAMDAILY_AUSGESETZT_STATUS = "ausgesetzt";

/**
 * Die Vorgangstypen, nach denen jeder Ticket-Block filtern lässt. Stichprobe
 * im Projekt MS am 2026-10-13: genau diese vier (plus interne MSTS-Typen, die
 * nie mitzählen, s. `istMstsTyp`).
 */
export const VORGANGSTYPEN = ["Incident", "Service Request", "Change", "Problem"] as const;
export const vorgangstypSchema = z.enum(VORGANGSTYPEN);
export type Vorgangstyp = z.infer<typeof vorgangstypSchema>;

/**
 * Ist das ein interner MSTS-Vorgangstyp? **Namensbasiert wie in eazyBI**
 * (`*MSTS*`), damit ein künftiger MSTS-Typ ohne Codeänderung herausfällt. Nicht
 * darauf verlassen, dass solche Vorgänge zufällig keine Kundenabteilung tragen.
 */
export function istMstsTyp(name: string | null): boolean {
  return /msts/i.test(name ?? "");
}

/** Board-Tag als `YYYY-MM-DD`; ohne Angabe zählt „heute". */
export const teamDailyDateSchema = z
  .string()
  .regex(/^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/, "Erwartet wird YYYY-MM-DD.")
  // Rundreise: „2026-02-31" hat die richtige Form, wäre aber für Postgres ein Fehler (500).
  .refine((s) => {
    const d = new Date(`${s}T12:00:00Z`);
    return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === s;
  }, "Kein gültiger Kalendertag.");

/**
 * Abwesenheit eines Mitarbeiters an genau diesem Tag — **nur der Tagesanteil**
 * (1 = ganzer Tag, 0.5 = halber), bewusst **ohne den Grund**. Der Abwesenheits-
 * grund (Urlaub, Krankheit …) ist eine besondere Datenkategorie und wird auf dem
 * für alle Rollen sichtbaren Board nicht ausgespielt — es zählt nur „(halb) da"
 * vs. „weg" für die Tagesplanung. Aus `employee_absence_days`, wo `(employee_id,
 * absence_date)` Primärschlüssel ist: höchstens eine Zeile je Tag.
 */
export const teamDailyAbsenceSchema = z.object({
  dayFraction: z.number(),
});
export type TeamDailyAbsence = z.infer<typeof teamDailyAbsenceSchema>;

/**
 * Ein Teammitglied des Boards. `jiraAccountId` wird aus `employees.email` über
 * die Jira-Nutzersuche aufgelöst (gecacht) — `null`, wenn Jira niemanden zu der
 * Adresse findet oder gerade nicht erreichbar ist. Ohne AccountId erscheint das
 * Mitglied als Spalte, trägt aber keine Tickets (kein Rateraten über Namen).
 */
export const teamDailyMemberSchema = z.object({
  employeeId: z.string().uuid(),
  fullName: z.string(),
  email: z.string(),
  jiraAccountId: z.string().nullable(),
  /** Abwesend an diesem Tag, sonst `null`. */
  absence: teamDailyAbsenceSchema.nullable(),
  /**
   * Hat dieses Mitglied am **Buchungstag** (`bookedOnDate`, der letzte
   * Arbeitstag vor dem Board-Tag) in Jira Zeit gebucht? Nur belastbar, wenn
   * `jiraAccountId` steht und Jira erreichbar war (`jiraLive`), sonst `false`.
   */
  bookedToday: z.boolean(),
  /**
   * Verfügbare Arbeitsstunden vom Board-Tag bis Freitag derselben Woche —
   * Arbeitstage nach Feiertagskalender, mal Stunden/Tag, abzüglich der
   * bekannten Abwesenheiten.
   *
   * Erst damit ist der Backlog zu lesen: „19 h offen" ist bei 32 verfügbaren
   * Stunden entspannt und bei 6 ein Problem. Ohne die Bezugsgröße ist die Zahl
   * nur groß oder klein, aber nicht gut oder schlecht.
   */
  capacityHours: z.number(),
});
export type TeamDailyMember = z.infer<typeof teamDailyMemberSchema>;

/**
 * Ein Vorgang auf dem Board — Kopfdaten plus die tagesrelevanten Live-Felder aus
 * `DAILY_BOARD_FIELDS`. `assigneeAccountId` verknüpft die Karte mit der Spalte
 * eines Mitglieds; `reporterIsCustomer` markiert „vom Kunden erstellt" (der
 * Melder ist ein JSM-Kunde, nicht ein interner Atlassian-Nutzer).
 */
export const teamDailyTicketSchema = z.object({
  key: z.string(),
  summary: z.string(),
  status: z.string().nullable(),
  statusCategory: z.string().nullable(),
  issueType: z.string().nullable(),
  priority: z.string().nullable(),
  assigneeAccountId: z.string().nullable(),
  assigneeName: z.string().nullable(),
  /** Melder ist ein JSM-Kunde → „vom Kunden erstellt". */
  reporterIsCustomer: z.boolean(),
  /** Restaufwand in Sekunden (`timetracking.remainingEstimateSeconds`). */
  remainingSeconds: z.number(),
  /**
   * Liegt der Vorgang bei jemand anderem — Kunde, Dritter, Terminwarten?
   *
   * Solche Vorgänge zählen **nicht** in den aktiven Backlog: Wer fünf Tickets
   * hat, von denen drei beim Kunden liegen, ist nicht ausgelastet, sondern
   * wartet. Vor dieser Trennung sah im Board jemand mit „34 h offen"
   * überlastet aus, obwohl die Hälfte davon gar nicht bei ihm lag.
   */
  isWaiting: z.boolean(),
  /** Anlagedatum (ISO) — daraus rechnet die Oberfläche das Alter im Eingang. */
  created: z.string(),
  /**
   * Anzeigename der Kundenfirma. Kommt aus dem bereits aufgelösten Ticketspiegel
   * (`jira_issues.kundenfirma_label`) — die Live-Suche liefert für Asset-Felder
   * nur die Objektnummer, keinen Klarnamen. `null`, solange ein frisches Ticket
   * noch nicht gespiegelt ist.
   */
  customerLabel: z.string().nullable(),
  updated: z.string().nullable(),
});
export type TeamDailyTicket = z.infer<typeof teamDailyTicketSchema>;

/**
 * Budgetzeile zum Board — je Serviceprojekt des Bereichs mit Restbudget. Aus
 * `service_projects`; rein informativ, kein Rückschreiben.
 */
export const teamDailyBudgetSchema = z.object({
  projectNumber: z.string(),
  title: z.string(),
  customerName: z.string().nullable(),
  budgetEur: z.number().nullable(),
  budgetLeftEur: z.number().nullable(),
  status: z.string(),
});
export type TeamDailyBudget = z.infer<typeof teamDailyBudgetSchema>;

/**
 * Antwort des Board-Endpoints. `jiraLive` sagt, ob die Live-Felder (Tickets,
 * „heute gebucht", Restaufwand) tatsächlich frisch aus Jira kamen; bei `false`
 * ist Jira nicht erreichbar oder nicht konfiguriert — Mitglieder, Abwesenheiten
 * und Budgets stehen dann trotzdem, `jiraError` trägt den Grund für die
 * Oberfläche.
 */
export const teamDailyBoardSchema = z.object({
  date: teamDailyDateSchema,
  segmentId: z.string().uuid(),
  segmentName: z.string(),
  members: z.array(teamDailyMemberSchema),
  tickets: z.array(teamDailyTicketSchema),
  /**
   * Der **Eingang**: offene Vorgänge, die noch **niemandem zugewiesen** sind
   * oder noch **nicht in Arbeit** stehen (Statuskategorie „zu erledigen").
   * Bewusst getrennt von `tickets` — das sind die Karten, die im Daily jemand
   * an sich ziehen muss, und genau die gehen unter, wenn sie in einer Spalte
   * neben den laufenden Tickets stehen.
   */
  intake: z.array(teamDailyTicketSchema),
  budgets: z.array(teamDailyBudgetSchema),
  /**
   * Der Tag, für den `bookedToday` gilt: der letzte **Arbeitstag vor** dem
   * Board-Tag. Nicht der Board-Tag selbst — morgens im Daily hat den laufenden
   * Tag noch niemand gebucht. Steht in der Antwort, damit die Oberfläche den
   * Tag benennen kann, statt „gestern" zu behaupten (am Montag ist es Freitag).
   */
  bookedOnDate: z.string(),
  /**
   * Basis-URL der Jira-Instanz, damit die Oberfläche Vorgangsnummern verlinken
   * kann (`<jiraBaseUrl>/browse/<key>`). `null`, wenn kein Zugang hinterlegt
   * ist — dann bleiben die Nummern Text statt ins Leere zu zeigen.
   */
  jiraBaseUrl: z.string().nullable(),
  jiraLive: z.boolean(),
  jiraError: z.string().nullable(),
  generatedAt: z.string(),
});
export type TeamDailyBoard = z.infer<typeof teamDailyBoardSchema>;

/**
 * Eine Person des Bereichs (Endpoint `/teamdaily/team`). Sie speist die
 * Auswahlliste beim Zusammenstellen eines Daily-Teams; wer hier nicht steht,
 * gehört nicht zum Bereich.
 */
export const teamDailyPersonSchema = z.object({
  employeeId: z.string().uuid(),
  fullName: z.string(),
  email: z.string(),
});
export type TeamDailyPerson = z.infer<typeof teamDailyPersonSchema>;

/** Alle Personen eines Bereichs, mit dem Namen des Bereichs. */
export const teamDailySegmentPeopleSchema = z.object({
  segmentId: z.string().uuid(),
  segmentName: z.string(),
  members: z.array(teamDailyPersonSchema),
});
export type TeamDailySegmentPeople = z.infer<typeof teamDailySegmentPeopleSchema>;

/* ----------------------------------------------------------- Daily-Teams */

/**
 * Ein **Daily-Team**: benannte Gruppe innerhalb eines Bereichs, an der die
 * Auswahl des Boards und die Notizen hängen (#264, seit 2026-10-01). Vorher
 * war die Auswahl eine private Liste im Browser, und die Notizen hingen am
 * Bereich — in „IT für HR" schrieben damit zwei Dailys auf denselben Zettel.
 */
export const dailyTeamSchema = z.object({
  id: z.string().uuid(),
  segmentId: z.string().uuid(),
  name: z.string(),
  members: z.array(teamDailyPersonSchema),
});
export type DailyTeam = z.infer<typeof dailyTeamSchema>;

/** Obergrenzen, damit ein Bereich nicht in Teams ertrinkt. */
export const DAILY_TEAM_NAME_MAX_LENGTH = 60;
export const DAILY_TEAM_MAX_MEMBERS = 50;

/**
 * Anlegen und Ändern eines Teams. Der Name ist Pflicht und je Bereich
 * eindeutig (Datenbank-Schlüssel): Zwei Teams „HR Daily" nebeneinander sind
 * eine Verwechslung, kein Komfortproblem.
 */
export const dailyTeamInputSchema = z.object({
  name: z.string().trim().min(1).max(DAILY_TEAM_NAME_MAX_LENGTH),
  memberIds: z.array(z.string().uuid()).max(DAILY_TEAM_MAX_MEMBERS),
});
export type DailyTeamInput = z.infer<typeof dailyTeamInputSchema>;

/* --------------------------------------------------------------- Notizen */

/** Obergrenzen, damit eine Notizliste nicht zur Datenhalde wird. */
export const TEAMDAILY_NOTE_MAX_LENGTH = 2000;
export const TEAMDAILY_NOTES_MAX_COUNT = 200;

/** Eine gespeicherte Tagesnotiz. */
export const teamDailyNoteSchema = z.object({
  id: z.string().uuid(),
  text: z.string(),
  done: z.boolean(),
  /** Reihenfolge innerhalb des Tages (0-basiert). */
  position: z.number().int(),
});
export type TeamDailyNote = z.infer<typeof teamDailyNoteSchema>;

/**
 * Eingabe einer einzelnen Notiz beim Speichern. Der Client schickt die
 * **ganze** Liste in der gewünschten Reihenfolge; die Position vergibt der
 * Server. Die `id` ist seit dem 2026-09-21 dabei: Eine Notiz lebt jetzt über
 * mehrere Tage, bis sie abgehakt ist, und der Server muss wissen, welche er
 * ändert. Ohne `id` ist sie neu. Leerer Text ist keine Notiz.
 */
export const teamDailyNoteInputSchema = z.object({
  id: z.string().uuid().optional(),
  text: z.string().trim().min(1).max(TEAMDAILY_NOTE_MAX_LENGTH),
  done: z.boolean().default(false),
});
export type TeamDailyNoteInput = z.infer<typeof teamDailyNoteInputSchema>;

/** Rumpf des Speichern-Aufrufs: die vollständige Notizliste des Tages. */
export const teamDailyNotesReplaceSchema = z.object({
  notes: z.array(teamDailyNoteInputSchema).max(TEAMDAILY_NOTES_MAX_COUNT),
});
export type TeamDailyNotesReplace = z.infer<typeof teamDailyNotesReplaceSchema>;

/* -------------------------------------------------------- Abwesenheiten */

/** Ein abwesender Tag eines Mitarbeiters: Datum + Tagesanteil (ohne Grund). */
export const teamDailyPresenceDaySchema = z.object({
  date: teamDailyDateSchema,
  dayFraction: z.number(),
});
export type TeamDailyPresenceDay = z.infer<typeof teamDailyPresenceDaySchema>;

/** Ein Teammitglied mit seinen abwesenden Tagen im Fenster. */
export const teamDailyPresenceMemberSchema = z.object({
  employeeId: z.string().uuid(),
  fullName: z.string(),
  days: z.array(teamDailyPresenceDaySchema),
});
export type TeamDailyPresenceMember = z.infer<
  typeof teamDailyPresenceMemberSchema
>;

/**
 * Abwesenheiten des Teams über ein Fenster (Standard: der Board-Tag und die
 * nächsten zwei Wochen). Aus `employee_absence_days`, das nächtlich aus Personio
 * kommt — **ohne Grund** gespeichert (immer „abwesend"), deshalb nur Tag und
 * Anteil, keine Abwesenheitsart. `from`/`to` sind einschließend.
 */
export const teamDailyPresenceSchema = z.object({
  from: teamDailyDateSchema,
  to: teamDailyDateSchema,
  members: z.array(teamDailyPresenceMemberSchema),
});
export type TeamDailyPresence = z.infer<typeof teamDailyPresenceSchema>;

/* ------------------------------------------------------------- News-Ticker */

/**
 * Eine Schlagzeile des Tickers. `isCustomer` unterscheidet eine Meldung zu einem
 * Kunden des Bereichs von einer generischen SAP/IT-Meldung — die Oberfläche
 * stellt Kundenmeldungen voran.
 */
export const teamDailyNewsItemSchema = z.object({
  title: z.string(),
  source: z.string(),
  /** Veröffentlichungsdatum als vom Feed gelieferte Zeichenkette. */
  date: z.string(),
  link: z.string(),
  isCustomer: z.boolean(),
  /**
   * Kam die Meldung **seit dem letzten Werktag** dazu — also seit dem letzten
   * Daily? `false` heißt: aus der Woche aufgefüllt, weil frisch zu wenig kam.
   */
  isFresh: z.boolean(),
});
export type TeamDailyNewsItem = z.infer<typeof teamDailyNewsItemSchema>;

/* ------------------------------------------------ Kennzahlen (Lieferung A) */

/** Die vier Zähler oben auf dem Board, in Anzeigereihenfolge. */
export const TEAMDAILY_ZAEHLER = [
  "ohneBearbeiter",
  "still",
  "aelterSechsWochen",
  "ausgesetzt",
] as const;
export const teamDailyZaehlerArtSchema = z.enum(TEAMDAILY_ZAEHLER);
export type TeamDailyZaehlerArt = z.infer<typeof teamDailyZaehlerArtSchema>;

/** Eine Zeile der Ticketliste hinter einer Zahl oder einem Balken. */
export const teamDailyKennzahlTicketSchema = z.object({
  key: z.string(),
  summary: z.string(),
  status: z.string().nullable(),
  issueType: z.string().nullable(),
  assigneeName: z.string().nullable(),
  customerLabel: z.string().nullable(),
  created: z.string(),
  updated: z.string().nullable(),
  /** Nur beim Zähler „Ausgesetzt": letzter Wechsel in einen Ausgesetzt-Status. */
  ausgesetztSeit: z.string().nullable(),
});
export type TeamDailyKennzahlTicket = z.infer<typeof teamDailyKennzahlTicketSchema>;

export const teamDailyZaehlerSchema = z.object({
  art: teamDailyZaehlerArtSchema,
  /** `null` = nicht bestimmbar (Jira nicht erreichbar, Status unbekannt) → „–". */
  anzahl: z.number().int().nullable(),
  /** Die JQL, mit der Jira gefragt wurde — für den Abgleich 1:1 in Jira nachstellbar. */
  jql: z.string().nullable(),
  tickets: z.array(teamDailyKennzahlTicketSchema),
  /** Mehr Treffer, als die Abfrage je Zähler lädt — die Zahl ist dann eine Untergrenze. */
  gekappt: z.boolean(),
});
export type TeamDailyZaehler = z.infer<typeof teamDailyZaehlerSchema>;

export const teamDailyKennzahlenSchema = z.object({
  /**
   * Worauf sich die Zähler beziehen: das gewählte Team oder der ganze Bereich
   * (Spec, Abgleich Nr. 1), und über wie viele verknüpfte Kundenabteilungen.
   */
  bezug: z.object({
    art: z.enum(["team", "bereich"]),
    abteilungen: z.number().int(),
  }),
  vorgangstyp: vorgangstypSchema.nullable(),
  zaehler: z.array(teamDailyZaehlerSchema),
  jiraBaseUrl: z.string().nullable(),
  jiraLive: z.boolean(),
  jiraError: z.string().nullable(),
  generatedAt: z.string(),
});
export type TeamDailyKennzahlen = z.infer<typeof teamDailyKennzahlenSchema>;

export const teamDailyVerlaufTagSchema = z.object({
  /** `YYYY-MM-DD`, Berliner Kalendertag. */
  tag: z.string(),
  erstellt: z.number().int(),
  geloest: z.number().int(),
  /** Offener Bestand am Ende des Tages. */
  offen: z.number().int(),
});
export type TeamDailyVerlaufTag = z.infer<typeof teamDailyVerlaufTagSchema>;

export const teamDailyVerlaufSchema = z.object({
  tage: z.array(teamDailyVerlaufTagSchema),
  /** Letzter Lauf des Spiegels für diese Abteilungen (ISO, UTC), sonst null. */
  stand: z.string().nullable(),
  jiraBaseUrl: z.string().nullable(),
});
export type TeamDailyVerlauf = z.infer<typeof teamDailyVerlaufSchema>;

export const teamDailyVerlaufTicketsSchema = z.object({
  tag: z.string(),
  erstellt: z.array(teamDailyKennzahlTicketSchema),
  geloest: z.array(teamDailyKennzahlTicketSchema),
  jiraBaseUrl: z.string().nullable(),
});
export type TeamDailyVerlaufTickets = z.infer<typeof teamDailyVerlaufTicketsSchema>;
